import { NurseLayout } from "@/components/layout/nurse-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useGetNurseDashboard, useListModules } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import {
  BookOpen,
  FlaskConical,
  GraduationCap,
  ArrowRight,
  MapPin,
  Bot,
  Activity,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  ShieldCheck,
  AlertTriangle,
  CalendarClock,
  Award,
  CheckCircle2,
  Circle,
  Clock3,
  Layers,
  Pencil,
  X,
  Sparkles,
  TestTube2,
  Plus,
  Trash2,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { PromotionBanners } from "@/components/ui/promotion-banner";
import { format, parseISO, differenceInDays, isValid, formatDistanceToNow } from "date-fns";
import { useState, useEffect, useCallback } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type EventType = "conference" | "webinar" | "seminar" | "workshop";

const TYPE_COLORS: Record<EventType, string> = {
  conference: "bg-teal-100 text-teal-700",
  webinar: "bg-indigo-100 text-indigo-700",
  seminar: "bg-amber-100 text-amber-700",
  workshop: "bg-rose-100 text-rose-700",
};

const DRUG_CLASS_COLORS: Record<string, string> = {
  "cDMARD": "bg-blue-50 text-blue-700 border-blue-200",
  "bDMARD": "bg-purple-50 text-purple-700 border-purple-200",
  "JAK inhibitor": "bg-violet-50 text-violet-700 border-violet-200",
  "Biologic": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Steroid": "bg-orange-50 text-orange-700 border-orange-200",
  "Bone protection": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Gout": "bg-yellow-50 text-yellow-700 border-yellow-200",
  "NSAID": "bg-rose-50 text-rose-700 border-rose-200",
};

function drugClassColor(cls: string) {
  for (const [key, val] of Object.entries(DRUG_CLASS_COLORS)) {
    if (cls.toLowerCase().includes(key.toLowerCase())) return val;
  }
  return "bg-slate-50 text-slate-700 border-slate-200";
}

// ---------------------------------------------------------------------------
// MonitoringCard (existing)
// ---------------------------------------------------------------------------
function MonitoringCard({ name, drugClass, monitoringRequirements, id }: {
  name: string;
  drugClass: string;
  monitoringRequirements: string;
  id: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const shortText = monitoringRequirements.length > 120
    ? monitoringRequirements.slice(0, 120) + "…"
    : monitoringRequirements;

  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/50 p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1 min-w-0">
          <Link href={`/nurse/pharmacology/${id}`} className="text-sm font-medium leading-snug hover:text-teal-600 transition-colors line-clamp-1">
            {name}
          </Link>
          <Badge variant="outline" className={`text-xs px-1.5 py-0 ${drugClassColor(drugClass)}`}>
            {drugClass}
          </Badge>
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className="shrink-0 mt-0.5 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>
      <p className="text-xs text-slate-600 leading-relaxed">
        {expanded ? monitoringRequirements : shortText}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Widget 1 — NMC Revalidation Countdown
// ---------------------------------------------------------------------------
const NMC_REVALIDATION_KEY = "rheumhub_nmc_revalidation_date";

const NMC_REQUIREMENTS = [
  "450 practice hours over 3 years",
  "35 CPD hours (incl. 20 hrs participatory)",
  "5 practice-related feedback items",
  "5 written reflective accounts",
  "Reflective discussion with NMC registrant",
  "Health & character declaration",
  "Professional indemnity confirmation",
  "Third-party confirmation (manager/assessor)",
];

function NmcRevalidationWidget() {
  const [storedDate, setStoredDate] = useState<string>(() => localStorage.getItem(NMC_REVALIDATION_KEY) ?? "");
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState(storedDate);
  const [showReqs, setShowReqs] = useState(false);

  const saveDate = useCallback(() => {
    if (inputVal) {
      localStorage.setItem(NMC_REVALIDATION_KEY, inputVal);
      setStoredDate(inputVal);
    }
    setEditing(false);
  }, [inputVal]);

  const clearDate = useCallback(() => {
    localStorage.removeItem(NMC_REVALIDATION_KEY);
    setStoredDate("");
    setInputVal("");
    setEditing(false);
  }, []);

  const parsed = storedDate ? new Date(storedDate) : null;
  const valid = parsed && isValid(parsed);
  const daysLeft = valid ? differenceInDays(parsed, new Date()) : null;

  let urgencyClass = "from-teal-50 to-cyan-50 border-teal-200";
  let urgencyBadge = "bg-teal-100 text-teal-700";
  let urgencyIcon = <ShieldCheck className="h-4 w-4 text-teal-600" />;
  let urgencyLabel = "On track";

  if (daysLeft !== null) {
    if (daysLeft < 0) {
      urgencyClass = "from-red-50 to-rose-50 border-red-200";
      urgencyBadge = "bg-red-100 text-red-700";
      urgencyIcon = <AlertTriangle className="h-4 w-4 text-red-600" />;
      urgencyLabel = "Overdue";
    } else if (daysLeft < 30) {
      urgencyClass = "from-red-50 to-orange-50 border-red-200";
      urgencyBadge = "bg-red-100 text-red-700";
      urgencyIcon = <AlertTriangle className="h-4 w-4 text-red-500" />;
      urgencyLabel = "Urgent";
    } else if (daysLeft < 90) {
      urgencyClass = "from-amber-50 to-yellow-50 border-amber-200";
      urgencyBadge = "bg-amber-100 text-amber-700";
      urgencyIcon = <CalendarClock className="h-4 w-4 text-amber-600" />;
      urgencyLabel = "Due soon";
    }
  }

  return (
    <Card className={`border bg-gradient-to-br ${urgencyClass}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {urgencyIcon}
            <CardTitle className="text-sm font-semibold">NMC Revalidation</CardTitle>
          </div>
          {valid && !editing && (
            <div className="flex items-center gap-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${urgencyBadge}`}>{urgencyLabel}</span>
              <button
                onClick={() => { setInputVal(storedDate); setEditing(true); }}
                className="text-slate-400 hover:text-slate-600 transition-colors p-0.5"
                aria-label="Edit revalidation date"
              >
                <Pencil className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {editing || !storedDate ? (
          <div className="space-y-2">
            <p className="text-xs text-slate-500">Set your NMC revalidation due date to track your countdown.</p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={inputVal}
                onChange={e => setInputVal(e.target.value)}
                className="flex-1 text-sm border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
              />
              <Button size="sm" onClick={saveDate} disabled={!inputVal} className="bg-teal-600 hover:bg-teal-700 text-white h-7 px-3 text-xs">
                Save
              </Button>
              {storedDate && (
                <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600" aria-label="Cancel">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {storedDate && (
              <button onClick={clearDate} className="text-xs text-red-500 hover:text-red-700 underline transition-colors">
                Clear date
              </button>
            )}
          </div>
        ) : valid && daysLeft !== null ? (
          <div className="space-y-2">
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold tabular-nums text-slate-800">
                {Math.abs(daysLeft)}
              </span>
              <span className="text-sm text-slate-500">
                {daysLeft < 0 ? "days overdue" : "days remaining"}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              Due: <span className="font-medium text-slate-700">{format(parsed, "d MMMM yyyy")}</span>
            </p>
            {daysLeft >= 0 && (
              <div className="w-full h-1.5 bg-white/60 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${daysLeft < 30 ? "bg-red-400" : daysLeft < 90 ? "bg-amber-400" : "bg-teal-500"}`}
                  style={{ width: `${Math.min(100, Math.max(2, ((1095 - daysLeft) / 1095) * 100))}%` }}
                />
              </div>
            )}
          </div>
        ) : null}

        <div>
          <button
            onClick={() => setShowReqs(r => !r)}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
          >
            {showReqs ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            NMC requirements checklist
          </button>
          {showReqs && (
            <ul className="mt-2 space-y-1">
              {NMC_REQUIREMENTS.map(r => (
                <li key={r} className="flex items-start gap-1.5 text-xs text-slate-600">
                  <Circle className="h-2.5 w-2.5 mt-0.5 shrink-0 text-slate-300" />
                  {r}
                </li>
              ))}
            </ul>
          )}
        </div>

        <a
          href="https://www.nmc.org.uk/revalidation/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium transition-colors"
        >
          NMC revalidation guide
          <ExternalLink className="h-3 w-3" />
        </a>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Widget 2 — Competency Portfolio Snapshot
// ---------------------------------------------------------------------------
type PracticeLevel = "all" | "specialist" | "advanced" | "consultant";

interface PortfolioDomainSummary {
  domain: { id: number; code: string; title: string };
  total: number;
  achieved: number;
  inProgress: number;
  notStarted: number;
}

interface PortfolioSummaryResponse {
  domains: PortfolioDomainSummary[];
  recentSignOffs: number;
}

const LEVEL_LABELS: Record<PracticeLevel, string> = {
  all: "All levels",
  specialist: "Specialist",
  advanced: "Advanced",
  consultant: "Consultant",
};

function CompetencySnapshotWidget() {
  const [data, setData] = useState<PortfolioSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [level, setLevel] = useState<PracticeLevel>("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    (async () => {
      try {
        const url = level === "all"
          ? "/api/competency/portfolio"
          : `/api/competency/portfolio?practiceLevel=${level}`;
        const res = await fetch(url);
        if (res.status === 401 || res.status === 403) {
          if (!cancelled) setFailed(true);
          return;
        }
        if (!res.ok) throw new Error("non-ok");
        const json: PortfolioSummaryResponse = await res.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [level]);

  const totals = data?.domains.reduce(
    (acc, d) => ({
      achieved: acc.achieved + d.achieved,
      inProgress: acc.inProgress + d.inProgress,
      notStarted: acc.notStarted + d.notStarted,
      total: acc.total + d.total,
    }),
    { achieved: 0, inProgress: 0, notStarted: 0, total: 0 }
  );

  const pct = totals && totals.total > 0 ? Math.round((totals.achieved / totals.total) * 100) : 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-purple-500" />
            <CardTitle className="text-sm font-semibold">Competency Portfolio</CardTitle>
          </div>
          <select
            value={level}
            onChange={e => setLevel(e.target.value as PracticeLevel)}
            className="text-xs border border-slate-200 rounded-md px-1.5 py-0.5 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-purple-400"
            aria-label="Filter by practice level"
          >
            {(Object.keys(LEVEL_LABELS) as PracticeLevel[]).map(l => (
              <option key={l} value={l}>{LEVEL_LABELS[l]}</option>
            ))}
          </select>
        </div>
        <CardDescription className="text-xs">RCN Rheumatology Framework progress</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="h-16 flex items-center justify-center">
            <div className="h-4 w-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : failed || !totals || !data ? (
          <p className="text-xs text-slate-400 py-2">Portfolio data unavailable — ensure your profile is set up.</p>
        ) : (
          <>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-bold text-slate-800 tabular-nums">{pct}%</span>
              <span className="text-xs text-slate-500">statements achieved</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 rounded-full transition-all"
                style={{ width: `${Math.max(pct, 1)}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-1 text-center">
              <div className="rounded-lg bg-emerald-50 py-2 space-y-0.5">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  <span className="text-sm font-semibold text-slate-800">{totals.achieved}</span>
                </div>
                <p className="text-xs text-slate-500">Achieved</p>
              </div>
              <div className="rounded-lg bg-amber-50 py-2 space-y-0.5">
                <div className="flex items-center justify-center gap-1">
                  <Clock3 className="h-3 w-3 text-amber-500" />
                  <span className="text-sm font-semibold text-slate-800">{totals.inProgress}</span>
                </div>
                <p className="text-xs text-slate-500">In progress</p>
              </div>
              <div className="rounded-lg bg-purple-50 py-2 space-y-0.5">
                <div className="flex items-center justify-center gap-1">
                  <CheckCircle2 className="h-3 w-3 text-purple-400" />
                  <span className="text-sm font-semibold text-slate-800">{data.recentSignOffs}</span>
                </div>
                <p className="text-xs text-slate-500">Signed off <span className="whitespace-nowrap">30d</span></p>
              </div>
            </div>
          </>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <Link
            href="/nurse/competency"
            className="flex items-center gap-1 group"
          >
            <span className="text-xs text-slate-500 group-hover:text-purple-600 transition-colors">Portfolio</span>
            <ArrowRight className="h-3 w-3 text-slate-400 group-hover:text-purple-600 transition-colors" />
          </Link>
          <Link
            href="/nurse/competency/plan"
            className="flex items-center gap-1 group"
          >
            <span className="text-xs text-slate-500 group-hover:text-purple-600 transition-colors">Dev plan</span>
            <ArrowRight className="h-3 w-3 text-slate-400 group-hover:text-purple-600 transition-colors" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Widget 3 — CPD Modules Discovery
// ---------------------------------------------------------------------------
const DIFFICULTY_COLORS: Record<string, string> = {
  foundation: "bg-blue-50 text-blue-700",
  intermediate: "bg-purple-50 text-purple-700",
  advanced: "bg-amber-50 text-amber-700",
};

function CpdModulesWidget() {
  const { data: modules, isLoading } = useListModules({ role: "nurse" });

  const totalPoints = modules?.reduce((sum, m) => sum + m.cpdPoints, 0) ?? 0;
  const totalMinutes = modules?.reduce((sum, m) => sum + m.estimatedMinutes, 0) ?? 0;
  const totalHours = Math.round((totalMinutes / 60) * 10) / 10;

  const byDifficulty = modules ? {
    foundation: modules.filter(m => m.difficulty === "foundation").length,
    intermediate: modules.filter(m => m.difficulty === "intermediate").length,
    advanced: modules.filter(m => m.difficulty === "advanced").length,
  } : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-indigo-500" />
          <CardTitle className="text-sm font-semibold">CPD Learning Modules</CardTitle>
        </div>
        <CardDescription className="text-xs">NMC-aligned continuing professional development</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="h-16 flex items-center justify-center">
            <div className="h-4 w-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-bold text-slate-800 tabular-nums">{modules?.length ?? 0}</span>
              <span className="text-xs text-slate-500">modules available</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="font-medium text-slate-700">{totalPoints} CPD pts</span>
              <span className="text-slate-300">·</span>
              <span>{totalHours} hrs learning</span>
            </div>
            {byDifficulty && (
              <div className="flex flex-wrap gap-1.5">
                {(["foundation", "intermediate", "advanced"] as const).map(d =>
                  byDifficulty[d] > 0 ? (
                    <span key={d} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${DIFFICULTY_COLORS[d]}`}>
                      {byDifficulty[d]} {d}
                    </span>
                  ) : null
                )}
              </div>
            )}
            {modules && modules.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {modules.slice(0, 2).map(m => (
                  <Link key={m.id} href={`/nurse/learn/modules/${m.id}`} className="flex items-center justify-between group">
                    <span className="text-xs text-slate-600 group-hover:text-indigo-600 transition-colors line-clamp-1">{m.title}</span>
                    <span className="text-xs text-slate-400 shrink-0 ml-2">{m.cpdPoints} pt{m.cpdPoints !== 1 ? "s" : ""}</span>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
        <Link
          href="/nurse/learn"
          className="flex items-center justify-between group pt-2 border-t border-slate-100"
        >
          <span className="text-xs text-slate-500 group-hover:text-indigo-600 transition-colors">Browse all modules</span>
          <ArrowRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-indigo-600 transition-colors" />
        </Link>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Widget 4 — Recent Guidelines Activity
// ---------------------------------------------------------------------------
const ORG_COLORS: Record<string, string> = {
  NICE:  "bg-blue-100 text-blue-700",
  BSR:   "bg-teal-100 text-teal-700",
  EULAR: "bg-violet-100 text-violet-700",
  ACR:   "bg-indigo-100 text-indigo-700",
  RCN:   "bg-rose-100 text-rose-700",
  ROS:   "bg-amber-100 text-amber-700",
};

function orgColor(org: string) {
  for (const [key, val] of Object.entries(ORG_COLORS)) {
    if (org.toUpperCase().includes(key)) return val;
  }
  return "bg-slate-100 text-slate-600";
}

interface RecentGuidelinesWidgetProps {
  guidelines: Array<{
    id: number;
    title: string;
    organisation: string;
    category: string;
    updatedAt: string;
  }>;
}

function RecentGuidelinesWidget({ guidelines }: RecentGuidelinesWidgetProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-blue-500" />
            <CardTitle className="text-sm font-semibold">Guidelines Activity</CardTitle>
          </div>
          <Link
            href="/nurse/guidelines"
            className="text-xs text-teal-600 hover:text-teal-800 font-medium transition-colors whitespace-nowrap"
          >
            View all
          </Link>
        </div>
        <CardDescription className="text-xs">Recently updated clinical guidance</CardDescription>
      </CardHeader>
      <CardContent className="space-y-0">
        {guidelines.length === 0 ? (
          <p className="text-xs text-slate-400 py-4 text-center">No guidelines available</p>
        ) : (
          <ul className="divide-y divide-slate-50">
            {guidelines.map(g => {
              const updated = parseISO(g.updatedAt);
              const relTime = isValid(updated)
                ? formatDistanceToNow(updated, { addSuffix: true })
                : null;
              return (
                <li key={g.id} className="py-2.5 first:pt-0 last:pb-0">
                  <Link href={`/nurse/guidelines/${g.id}`} className="flex items-start gap-2.5 group">
                    <div className="min-w-0 flex-1 space-y-1">
                      <p className="text-xs font-medium leading-snug text-slate-800 group-hover:text-teal-600 transition-colors line-clamp-2">
                        {g.title}
                      </p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`inline-block text-xs px-1.5 py-0 rounded-sm font-medium leading-5 ${orgColor(g.organisation)}`}>
                          {g.organisation}
                        </span>
                        <span className="text-xs text-slate-400">{g.category}</span>
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1 pt-0.5">
                      {relTime && (
                        <span className="text-xs text-slate-400 whitespace-nowrap">{relTime}</span>
                      )}
                      <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-teal-500 transition-colors" />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
        <div className="pt-3 border-t border-slate-100 mt-2">
          <Link
            href="/nurse/guidelines"
            className="flex items-center justify-between group"
          >
            <span className="text-xs text-slate-500 group-hover:text-teal-600 transition-colors">Browse all guidelines</span>
            <ArrowRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-teal-600 transition-colors" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Widget 5 — Monitoring Due Dates tracker
// ---------------------------------------------------------------------------
const MONITORING_ENTRIES_KEY = "rheumhub_monitoring_entries";

interface MonitoringEntry {
  id: string;
  label: string;
  dueDate: string;
}

function monitoringUrgency(daysLeft: number) {
  if (daysLeft < 0) return { badge: "bg-red-100 text-red-700", icon: <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />, label: "Overdue" };
  if (daysLeft <= 7) return { badge: "bg-red-100 text-red-700", icon: <AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />, label: `${daysLeft}d` };
  if (daysLeft <= 21) return { badge: "bg-amber-100 text-amber-700", icon: <CalendarClock className="h-3 w-3 text-amber-500 shrink-0" />, label: `${daysLeft}d` };
  return { badge: "bg-emerald-100 text-emerald-700", icon: <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />, label: `${daysLeft}d` };
}

function MonitoringDueDatesWidget() {
  const [entries, setEntries] = useState<MonitoringEntry[]>(() => {
    try {
      const raw = localStorage.getItem(MONITORING_ENTRIES_KEY);
      return raw ? (JSON.parse(raw) as MonitoringEntry[]) : [];
    } catch {
      return [];
    }
  });
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newDate, setNewDate] = useState("");

  const persistEntries = useCallback((next: MonitoringEntry[]) => {
    setEntries(next);
    localStorage.setItem(MONITORING_ENTRIES_KEY, JSON.stringify(next));
  }, []);

  const addEntry = useCallback(() => {
    if (!newLabel.trim() || !newDate) return;
    const entry: MonitoringEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      label: newLabel.trim(),
      dueDate: newDate,
    };
    persistEntries([...entries, entry].sort((a, b) => a.dueDate.localeCompare(b.dueDate)));
    setNewLabel("");
    setNewDate("");
    setAdding(false);
  }, [entries, newLabel, newDate, persistEntries]);

  const removeEntry = useCallback((id: string) => {
    persistEntries(entries.filter(e => e.id !== id));
  }, [entries, persistEntries]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const overdueCount = entries.filter(e => {
    const d = new Date(e.dueDate);
    return isValid(d) && differenceInDays(d, today) < 0;
  }).length;

  return (
    <Card className={overdueCount > 0 ? "border-red-200 bg-gradient-to-br from-red-50 to-rose-50" : ""}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <TestTube2 className="h-4 w-4 text-amber-500" />
            <CardTitle className="text-sm font-semibold">Monitoring Due Dates</CardTitle>
          </div>
          {overdueCount > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">
              {overdueCount} overdue
            </span>
          )}
        </div>
        <CardDescription className="text-xs">Track upcoming patient monitoring checks</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {entries.length === 0 && !adding ? (
          <p className="text-xs text-slate-400 py-2">No monitoring entries yet. Add a patient check below.</p>
        ) : (
          <ul className="space-y-1.5">
            {entries.map(e => {
              const d = new Date(e.dueDate);
              const daysLeft = isValid(d) ? differenceInDays(d, today) : 0;
              const { badge, icon, label } = monitoringUrgency(daysLeft);
              return (
                <li key={e.id} className="flex items-center gap-2 group">
                  {icon}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{e.label}</p>
                    <p className="text-xs text-slate-400">{isValid(d) ? format(d, "d MMM yyyy") : e.dueDate}</p>
                  </div>
                  <span className={`text-xs font-medium px-1.5 py-0 rounded-full ${badge} shrink-0`}>
                    {label}
                  </span>
                  <button
                    onClick={() => removeEntry(e.id)}
                    className="shrink-0 text-slate-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                    aria-label={`Remove ${e.label}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {adding ? (
          <div className="space-y-2 pt-1 border-t border-slate-100">
            <input
              type="text"
              placeholder="e.g. Patient A — MTX FBC"
              value={newLabel}
              onChange={e => setNewLabel(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
              autoFocus
            />
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={newDate}
                onChange={e => setNewDate(e.target.value)}
                className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              <Button
                size="sm"
                onClick={addEntry}
                disabled={!newLabel.trim() || !newDate}
                className="bg-amber-500 hover:bg-amber-600 text-white h-7 px-3 text-xs shrink-0"
              >
                Add
              </Button>
              <button
                onClick={() => { setAdding(false); setNewLabel(""); setNewDate(""); }}
                className="text-slate-400 hover:text-slate-600 shrink-0"
                aria-label="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-600 transition-colors pt-1 border-t border-slate-100 w-full"
          >
            <Plus className="h-3.5 w-3.5" />
            Add monitoring entry
          </button>
        )}

        <div className="pt-2 border-t border-slate-100">
          <Link
            href="/nurse/pharmacology"
            className="flex items-center justify-between group"
          >
            <span className="text-xs text-slate-500 group-hover:text-amber-600 transition-colors">Monitoring protocols</span>
            <ArrowRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-amber-600 transition-colors" />
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Widget 6 — AI Clinical Assistant Shortcut
// ---------------------------------------------------------------------------
const AI_QUICK_PROMPTS = [
  { label: "MTX monitoring schedule", q: "What is the standard monitoring schedule for methotrexate in rheumatoid arthritis?" },
  { label: "Biologic contraindications", q: "What are the key contraindications for biologic DMARDs in rheumatoid arthritis?" },
  { label: "NMC delegation advice", q: "What does the NMC Code say about delegation and accountability for nurse prescribers?" },
  { label: "BSR rituximab guidance", q: "Summarise BSR guidance on rituximab use in rheumatoid arthritis including monitoring requirements." },
];

function AiChatShortcutWidget() {
  return (
    <Card className="border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-600 text-white shrink-0">
            <Bot className="h-3.5 w-3.5" />
          </div>
          <CardTitle className="text-sm font-semibold">AI Clinical Assistant</CardTitle>
        </div>
        <CardDescription className="text-xs">
          NMC Code · NICE · BSR · EULAR — evidence-based answers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          {AI_QUICK_PROMPTS.map(p => (
            <Link
              key={p.label}
              href={`/nurse/chat?q=${encodeURIComponent(p.q)}`}
              className="flex items-center gap-2 rounded-md bg-white/70 hover:bg-white px-2.5 py-1.5 transition-colors group border border-teal-100 hover:border-teal-300"
            >
              <Sparkles className="h-3 w-3 text-teal-400 shrink-0" />
              <span className="text-xs text-slate-600 group-hover:text-teal-700 transition-colors">{p.label}</span>
              <ArrowRight className="h-3 w-3 text-slate-300 group-hover:text-teal-500 ml-auto transition-colors" />
            </Link>
          ))}
        </div>
        <Link href="/nurse/chat" className="block pt-1">
          <Button size="sm" className="w-full bg-teal-600 hover:bg-teal-700 text-white gap-1.5 text-xs h-8">
            <Bot className="h-3.5 w-3.5" />
            Open AI assistant
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Dashboard
// ---------------------------------------------------------------------------
export default function NurseDashboard() {
  // On advicelinehub.co.uk, redirect straight to the incoming requests page
  // — no RheumHub dashboard with clinical tools here.
  const [, navigate] = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hostname.includes("advicelinehub")) {
      navigate("/nurse/requests");
    }
  }, [navigate]);

  const { data: dashboard, isLoading, error } = useGetNurseDashboard();

  if (isLoading) return <NurseLayout title="Dashboard"><LoadingScreen /></NurseLayout>;
  if (error || !dashboard) return <NurseLayout title="Dashboard"><div>Error loading dashboard</div></NurseLayout>;

  return (
    <NurseLayout title="Dashboard">
      <PromotionBanners audience="nurse" />

      {/* Stats row */}
      <div className="grid gap-6 md:grid-cols-3 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Guidelines</CardTitle>
            <BookOpen className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.totalGuidelines}</div>
            <p className="text-xs text-slate-500">Active clinical guidelines</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pharmacology</CardTitle>
            <FlaskConical className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.totalMedications}</div>
            <p className="text-xs text-slate-500">Drugs in reference</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Scenarios</CardTitle>
            <GraduationCap className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard.totalScenarios}</div>
            <p className="text-xs text-slate-500">NMC & Advice line cases</p>
          </CardContent>
        </Card>
      </div>

      {/* Professional development widgets row */}
      <div className="grid gap-6 md:grid-cols-3 mb-6">
        <NmcRevalidationWidget />
        <CompetencySnapshotWidget />
        <CpdModulesWidget />
      </div>

      {/* New dashboard widgets row: guidelines activity, monitoring due dates, AI shortcut */}
      <div className="grid gap-6 md:grid-cols-3 mb-8">
        <RecentGuidelinesWidget guidelines={dashboard.recentGuidelines} />
        <MonitoringDueDatesWidget />
        <AiChatShortcutWidget />
      </div>

      {/* Main content grid */}
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {/* Recent Guidelines */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Guidelines</CardTitle>
            <CardDescription>Latest updates from NICE, BSR, and others</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            {dashboard.recentGuidelines.map(g => (
              <Link key={g.id} href={`/nurse/guidelines/${g.id}`} className="flex items-center justify-between group">
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none group-hover:text-teal-600 transition-colors">{g.title}</p>
                  <p className="text-sm text-slate-500">{g.organisation} · {g.category}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-teal-600 transition-colors" />
              </Link>
            ))}
          </CardContent>
        </Card>

        {/* Featured Scenarios */}
        <Card>
          <CardHeader>
            <CardTitle>Featured Scenarios</CardTitle>
            <CardDescription>Practice clinical reasoning</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4">
            {dashboard.featuredScenarios.map(s => (
              <Link key={s.id} href={`/nurse/scenarios/${s.id}`} className="flex items-center justify-between group">
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none group-hover:text-teal-600 transition-colors">{s.title}</p>
                  <p className="text-sm text-slate-500 capitalize">{s.difficulty} · {s.type.replace("_", " ")}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-teal-600 transition-colors" />
              </Link>
            ))}
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-2">
            <div>
              <CardTitle>Upcoming Events</CardTitle>
              <CardDescription>CPD &amp; professional development</CardDescription>
            </div>
            <Link href="/nurse/events" className="text-xs text-teal-600 hover:text-teal-800 font-medium whitespace-nowrap mt-1 transition-colors">
              View all
            </Link>
          </CardHeader>
          <CardContent className="grid gap-4">
            {dashboard.upcomingEvents.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No upcoming events</p>
            ) : (
              dashboard.upcomingEvents.map(event => (
                <a
                  key={event.id}
                  href={event.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 group"
                >
                  <div className={`mt-0.5 rounded-md px-1.5 py-0.5 text-xs font-semibold shrink-0 ${TYPE_COLORS[event.type as EventType] ?? "bg-slate-100 text-slate-700"}`}>
                    {format(parseISO(event.date), "d MMM")}
                  </div>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="text-sm font-medium leading-snug group-hover:text-teal-600 transition-colors line-clamp-1">
                      {event.title}
                    </p>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <MapPin className="h-3 w-3 shrink-0" />
                      {event.location ?? "Online"} · {event.organiser}
                    </p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-slate-300 group-hover:text-teal-500 shrink-0 mt-0.5 transition-colors" />
                </a>
              ))
            )}
            <Link
              href="/nurse/events"
              className="flex items-center justify-between group pt-1 border-t border-slate-100"
            >
              <span className="text-xs text-slate-500 group-hover:text-teal-600 transition-colors">Browse all events</span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-teal-600 transition-colors" />
            </Link>
          </CardContent>
        </Card>

        {/* Drug Monitoring Quick Reference — spans full width on xl */}
        <Card className="md:col-span-2 xl:col-span-3">
          <CardHeader className="flex flex-row items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-amber-500" />
                <CardTitle>Monitoring Quick Reference</CardTitle>
              </div>
              <CardDescription className="mt-1">
                Key monitoring requirements for commonly used rheumatology drugs — tap any card to expand
              </CardDescription>
            </div>
            <Link href="/nurse/pharmacology" className="text-xs text-teal-600 hover:text-teal-800 font-medium whitespace-nowrap mt-1 transition-colors">
              Full pharmacology
            </Link>
          </CardHeader>
          <CardContent>
            {dashboard.monitoringHighlights.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No monitoring data available</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {dashboard.monitoringHighlights.map(m => (
                  <MonitoringCard key={m.id} {...m} />
                ))}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-400">
                Always verify against current BNF, BSR shared care guidelines, and local trust protocols
              </p>
              <Link href="/nurse/pharmacology" className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium transition-colors">
                View all drugs
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </NurseLayout>
  );
}
